/**
 * In-browser video export utilities for SaaS mode.
 *
 * Builds a composition component from RemotionSceneDescription[] that
 * renderMediaOnWeb() can render, and provides a browser download trigger.
 *
 * Audio clips are injected as Remotion <Audio> components so that
 * renderMediaOnWeb() encodes them directly into the MP4, no separate
 * audio mixing step needed.
 */

import type {AudioClipConfig, AudioTrackConfig, RemotionSceneDescription} from '@cascade/core';
import React from 'react';
import {AbsoluteFill, Audio, Sequence} from 'remotion';

interface SceneEntry {
  firstFrame: number;
  durationInFrames: number;
  Component: React.LazyExoticComponent<React.ComponentType>;
}

interface AudioEntry {
  src: string;
  startFrame: number;
  volume: number;
}

/**
 * Build a React component that lays out all scenes as Remotion Sequences,
 * with audio clips injected as <Audio> elements for native encoding.
 *
 * Same layout pattern as RemotionSequencer in RemotionPreview.tsx,
 * ensuring what-you-see-is-what-you-export.
 */
export function buildExportComposition(
  remotionScenes: RemotionSceneDescription[],
  fps: number,
  audioClips?: AudioClipConfig[],
  audioTracks?: AudioTrackConfig[],
): React.FC {
  // Pre-compute scene entries with cumulative frame offsets
  let offset = 0;
  const entries: SceneEntry[] = remotionScenes.map(scene => {
    const entry: SceneEntry = {
      firstFrame: offset,
      durationInFrames: scene.durationInFrames,
      Component: React.lazy(scene.component),
    };
    offset += scene.durationInFrames;
    return entry;
  });

  // Build audio entries: filter muted tracks, apply per-track volume
  const trackMap = new Map((audioTracks ?? []).map(t => [t.id, t]));
  const audioEntries: AudioEntry[] = (audioClips ?? [])
    .filter(clip => {
      const track = trackMap.get(clip.trackId);
      return track && !track.muted;
    })
    .map(clip => {
      const track = trackMap.get(clip.trackId)!;
      return {
        src: clip.src,
        startFrame: Math.round((clip.offset / 1000) * fps),
        volume: track.volume ?? 1,
      };
    });

  // Return a component that renders scenes + audio
  const ExportComposition: React.FC = () =>
    React.createElement(
      AbsoluteFill,
      null,
      // Scene sequences
      ...entries.map((scene, i) =>
        React.createElement(
          Sequence,
          {key: `scene-${i}`, from: scene.firstFrame, durationInFrames: scene.durationInFrames},
          React.createElement(
            React.Suspense,
            {fallback: React.createElement(AbsoluteFill, {style: {backgroundColor: '#000'}})},
            React.createElement(scene.Component),
          ),
        ),
      ),
      // Audio clips as Remotion <Audio> elements
      ...audioEntries.map((audio, i) =>
        React.createElement(
          Sequence,
          {key: `audio-${i}`, from: audio.startFrame},
          React.createElement(Audio, {
            src: audio.src,
            volume: audio.volume,
          }),
        ),
      ),
    );

  return ExportComposition;
}

/**
 * Trigger a browser file download from a Blob.
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Delay revoke to ensure download starts
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
